import { Modal } from "antd";
import classNames from "classnames";
import { ReactNode, useState } from "react";

import VLButton from "views/uikit/Buttons/VLButton";
import LightbulbIcon from "assets/icons/LightbulbIcon";

import styles from "./style.module.scss";

export interface GuideTopic {
  key: string;
  label: string;
  title: string;
  body: ReactNode;
  tip?: ReactNode;
  art: ReactNode;
}

interface GuideBookProps {
  open: boolean;
  onClose: () => void;
  heading: string;
  topics: GuideTopic[];
  hint?: string;
}

// Reference-style guide: pick a topic on the left, read about it on the right.
const GuideBook = ({
  open,
  onClose,
  heading,
  topics,
  hint = "Reopen this guide any time from the ? next to the page title.",
}: GuideBookProps) => {
  const [activeKey, setActiveKey] = useState(topics[0]?.key);
  const topic = topics.find((t) => t.key === activeKey) ?? topics[0];

  const close = () => {
    setActiveKey(topics[0]?.key);
    onClose();
  };

  if (!topic) return null;

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      centered
      width={960}
      destroyOnClose
      className={classNames(styles.guideModal, styles.book)}
    >
      <div className={styles.bookGrid} data-testid="guide-book">
        <nav className={styles.tabs} aria-label={heading}>
          <div className={styles.tabsHeading}>{heading}</div>
          {topics.map((t) => (
            <button
              key={t.key}
              type="button"
              className={classNames(styles.tab, {
                [styles.tabActive]: t.key === topic.key,
              })}
              aria-current={t.key === topic.key}
              onClick={() => setActiveKey(t.key)}
            >
              {t.label}
            </button>
          ))}
        </nav>
        <div className={styles.main}>
          <div className={styles.content}>
            <h2 key={`t-${topic.key}`} className={styles.title}>
              {topic.title}
            </h2>
            <div key={`b-${topic.key}`} className={styles.body}>
              {topic.body}
            </div>
            {topic.tip && (
              <div key={`tip-${topic.key}`} className={styles.tip}>
                <span className={styles.tipIcon}>
                  <LightbulbIcon />
                </span>
                <span>{topic.tip}</span>
              </div>
            )}
          </div>
          <div className={styles.footer}>
            <span className={styles.bookHint}>{hint}</span>
            <VLButton
              type="primary"
              size="middle"
              className={styles.footerButton}
              text="Got it"
              onClick={close}
            />
          </div>
        </div>
        <div className={styles.hero} aria-hidden="true">
          <div key={`art-${topic.key}`} className={styles.art}>
            {topic.art}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default GuideBook;
